import clsx from "clsx";
import { Paperclip, X } from "lucide-react";
import {
  useState,
  useRef,
  useEffect,
  type FormEvent,
  type ChangeEvent,
  type ClipboardEvent,
  type DragEvent,
  type KeyboardEvent,
  type PointerEvent,
} from "react";

import { useDictation } from "@/hooks/useDictation";
import { useModels } from "@/hooks/useModels";
import { useCreateJob } from "@/hooks/useSession";
import { uploadAttachments } from "@/lib/api";
import { formatAttachmentSize, mergeAttachmentFiles } from "@/lib/attachments";
import { useStore } from "@/lib/store";

import { DictationButton } from "./DictationButton";
import { DictationStatus } from "./DictationStatus";
import { SendButton } from "./SendButton";
import { getModelLabel } from "./stats/formatters";

const TEXTAREA_ID = "mako-message";

interface MessageFormProps {
  autoFocus?: boolean;
}

function autoResizeTextarea(textarea: HTMLTextAreaElement): void {
  textarea.style.height = "auto";
  textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
}

export function MessageForm({ autoFocus }: MessageFormProps): JSX.Element {
  const [message, setMessage] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const mode = useStore((state) => state.composerMode);
  const setMode = useStore((state) => state.setComposerMode);
  const modelId = useStore((state) => state.composerModelId);
  const setModelId = useStore((state) => state.setComposerModelId);
  const showToast = useStore((state) => state.showToast);
  const { data: modelsData, isLoading: isModelsLoading } = useModels();
  const createJob = useCreateJob();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dictation = useDictation({
    onTranscript: (text) => {
      insertTranscriptAtCursor(text);
    },
    onError: (errorMessage) => {
      showToast(errorMessage, "error");
    },
  });

  const isRecording = dictation.status === "recording";
  const isProcessingAudio =
    dictation.status === "transcribing" || dictation.status === "processing";
  const isSubmitting = createJob.isPending || isUploading;
  const isBusy = isSubmitting || isProcessingAudio;
  const selectedModelId = modelId ?? modelsData?.defaultModelId ?? "";

  useEffect(() => {
    if (!autoFocus) return;
    if (isBusy) return;
    // Defer to avoid focusing before layout / route transitions finish.
    const id = window.setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(id);
  }, [autoFocus, isBusy]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    autoResizeTextarea(textarea);
  }, [message]);

  useEffect(() => {
    if (modelId || !modelsData?.defaultModelId) {
      return;
    }

    setModelId(modelsData.defaultModelId);
  }, [modelId, modelsData?.defaultModelId, setModelId]);

  function handleModeChange(nextMode: "build" | "plan"): void {
    if (mode === nextMode) {
      return;
    }

    setMode(nextMode);
  }

  function handleModelChange(nextModelId: string): void {
    const normalized = nextModelId.trim() || null;
    if ((modelId ?? null) === normalized) {
      return;
    }

    setModelId(normalized);
  }

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!message.trim() || isBusy || isRecording) return;

    try {
      let attachmentIds: string[] | undefined;
      if (selectedFiles.length > 0) {
        setIsUploading(true);
        const uploadResponse = await uploadAttachments(selectedFiles);
        attachmentIds = uploadResponse.attachments.map((attachment) => attachment.id);
      }

      await createJob.mutateAsync({
        message: message.trim(),
        mode,
        modelId: selectedModelId || undefined,
        attachmentIds,
      });
      setMessage("");
      setSelectedFiles([]);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to send message";
      showToast(errorMessage, "error");
    } finally {
      setIsUploading(false);
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>): void {
    if (e.key !== "Enter") {
      return;
    }

    if (e.ctrlKey) {
      e.preventDefault();
      const textarea = e.currentTarget;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const nextMessage = `${message.slice(0, start)}\n${message.slice(end)}`;
      setMessage(nextMessage);
      requestAnimationFrame(() => {
        textarea.selectionStart = start + 1;
        textarea.selectionEnd = start + 1;
        autoResizeTextarea(textarea);
      });
      return;
    }

    if (e.shiftKey) {
      return;
    }

    e.preventDefault();
    if (message.trim() && !isBusy && !isRecording) {
      void handleSubmit(e as unknown as FormEvent);
    }
  }

  function insertTranscriptAtCursor(transcript: string): void {
    const normalizedTranscript = transcript.trim();
    if (!normalizedTranscript) {
      return;
    }

    const textarea = textareaRef.current;
    if (!textarea) {
      setMessage((current) =>
        current.trim().length > 0 ? `${current} ${normalizedTranscript}` : normalizedTranscript,
      );
      return;
    }

    const selectionStart = textarea.selectionStart ?? textarea.value.length;
    const selectionEnd = textarea.selectionEnd ?? textarea.value.length;

    setMessage((current) => {
      const start = Math.min(selectionStart, current.length);
      const end = Math.min(selectionEnd, current.length);
      const before = current.slice(0, start);
      const after = current.slice(end);
      const prefix = before.length > 0 && !/\s$/.test(before) ? " " : "";
      const suffix = after.length > 0 && !/^\s/.test(after) ? " " : "";
      const insertion = `${prefix}${normalizedTranscript}${suffix}`;
      const nextMessage = `${before}${insertion}${after}`;
      const nextCursor = before.length + insertion.length;

      requestAnimationFrame(() => {
        const target = textareaRef.current;
        if (!target) return;
        target.focus();
        target.selectionStart = nextCursor;
        target.selectionEnd = nextCursor;
        autoResizeTextarea(target);
      });

      return nextMessage;
    });
  }

  function addFiles(incomingFiles: File[]): void {
    const { files, error } = mergeAttachmentFiles(selectedFiles, incomingFiles);
    if (error) {
      showToast(error, "error");
      return;
    }
    setSelectedFiles(files);
  }

  function handleFileChange(e: ChangeEvent<HTMLInputElement>): void {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      addFiles(files);
    }
    // Reset so selecting the same file again triggers change.
    e.target.value = "";
  }

  function handlePaste(e: ClipboardEvent<HTMLTextAreaElement>): void {
    const files = Array.from(e.clipboardData.files || []);
    if (files.length === 0) {
      return;
    }

    e.preventDefault();
    addFiles(files);
  }

  function handleDragOver(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setIsDraggingFiles(true);
  }

  function handleDragLeave(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) {
      return;
    }
    setIsDraggingFiles(false);
  }

  function handleDrop(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setIsDraggingFiles(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      addFiles(files);
    }
  }

  function removeSelectedFile(index: number): void {
    setSelectedFiles((current) => current.filter((_, currentIndex) => currentIndex !== index));
  }

  function focusTextarea(): void {
    textareaRef.current?.focus();
  }

  function handleContainerPointerDown(e: PointerEvent<HTMLDivElement>): void {
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest("textarea,button,a,input,select")) return;
    e.preventDefault();
    focusTextarea();
  }

  return (
    <div className="space-y-3">
      {/* Mode toggle - above input */}
      <div className="flex flex-wrap items-center justify-center gap-2">
        <button
          type="button"
          onClick={() => handleModeChange("build")}
          className={clsx(
            "rounded-full px-3 py-1 text-xs font-medium transition-all",
            mode === "build"
              ? "border border-cyber-magenta/50 bg-cyber-magenta/20 text-cyber-magenta"
              : "border border-transparent text-cyber-muted hover:text-cyber-text",
          )}
          title="Build mode: can make changes"
        >
          BUILD
        </button>
        <button
          type="button"
          onClick={() => handleModeChange("plan")}
          className={clsx(
            "rounded-full px-3 py-1 text-xs font-medium transition-all",
            mode === "plan"
              ? "border border-cyber-cyan/50 bg-cyber-cyan/20 text-cyber-cyan"
              : "border border-transparent text-cyber-muted hover:text-cyber-text",
          )}
          title="Plan mode: explore without making changes"
        >
          PLAN
        </button>
        <div className="relative">
          <select
            value={selectedModelId}
            onChange={(e) => handleModelChange(e.target.value)}
            className="rounded-full border border-cyber-border bg-cyber-surface px-3 py-1 text-xs text-cyber-text transition-all hover:border-cyber-cyan/40 focus:border-cyber-cyan/60 focus:outline-none"
            title="Model for this new chat"
            aria-label="Model for this new chat"
            disabled={isBusy || isModelsLoading || !modelsData || modelsData.models.length === 0}
          >
            {!selectedModelId && <option value="">OpenCode default</option>}
            {isModelsLoading && <option value="">Loading models...</option>}
            {!isModelsLoading && modelsData?.models.length === 0 && (
              <option value="">No models available</option>
            )}
            {modelsData?.models.map((model) => (
              <option key={model.fullId} value={model.fullId}>
                {`${model.providerName} · ${getModelLabel(model.providerId, model.modelId, model.modelName)}`}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit}>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          multiple
          onChange={handleFileChange}
          disabled={isBusy}
        />

        <div
          className={clsx(
            "focus-ring min-h-[60px] cursor-text rounded-2xl border border-cyber-border bg-cyber-surface px-4 py-3",
            isDraggingFiles && "border-cyber-cyan/60 bg-cyber-cyan/5",
          )}
          onPointerDown={handleContainerPointerDown}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="flex items-center gap-3">
            <span className="text-lg text-cyber-cyan">{">"}</span>
            <textarea
              id={TEXTAREA_ID}
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onInput={(e) => autoResizeTextarea(e.currentTarget)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder="Ask Mako something..."
              required
              rows={1}
              className="flex-1 cursor-text resize-none overflow-y-auto rounded-none border-none bg-transparent text-sm text-cyber-text placeholder-cyber-muted/50 focus:shadow-none focus:outline-none focus:ring-0"
              autoComplete="off"
              disabled={isBusy}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isBusy || isRecording}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-cyber-border text-cyber-muted transition-all hover:border-cyber-cyan/50 hover:text-cyber-cyan"
              title="Attach files"
              aria-label="Attach files"
            >
              <Paperclip size={16} />
            </button>
            <DictationButton
              variant="cyan"
              status={dictation.status}
              isSupported={dictation.isSupported}
              disabled={isBusy}
              onClick={() => {
                void dictation.toggleRecording();
              }}
              onPointerDown={(e) => {
                e.preventDefault();
                focusTextarea();
              }}
            />
            <SendButton
              variant="cyan"
              disabled={isBusy || isRecording || !message.trim()}
              pending={isSubmitting}
              onPointerDown={(e) => {
                // Keep focus in the textarea when clicking send.
                e.preventDefault();
                focusTextarea();
              }}
            />
          </div>

          <DictationStatus
            variant="cyan"
            status={dictation.status}
            elapsedMs={dictation.elapsedMs}
            level={dictation.level}
          />

          {!dictation.isSupported && (
            <div className="mt-2 text-[11px] text-cyber-muted">
              Voice dictation is unavailable in this browser.
            </div>
          )}

          {(selectedFiles.length > 0 || isDraggingFiles) && (
            <div className="mt-2 border-t border-cyber-border/60 pt-2">
              {selectedFiles.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {selectedFiles.map((file, index) => (
                    <div
                      key={`${file.name}:${file.size}:${file.lastModified}`}
                      className="inline-flex items-center gap-2 rounded-full border border-cyber-border bg-cyber-bg/60 px-3 py-1 text-xs text-cyber-text"
                    >
                      <span className="max-w-[180px] truncate" title={file.name}>
                        {file.name}
                      </span>
                      <span className="text-cyber-muted">{formatAttachmentSize(file.size)}</span>
                      <button
                        type="button"
                        onClick={() => removeSelectedFile(index)}
                        className="text-cyber-muted transition-all hover:text-cyber-red"
                        aria-label={`Remove ${file.name}`}
                        title="Remove"
                        disabled={isBusy}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-cyber-muted">Drop files to attach</div>
              )}
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
